import { useFormik } from 'formik';
import { Filter, RefreshCw, Search } from 'lucide-react';
import styled from 'styled-components';

import { Button } from '../../../components/atoms/button/button';
import { DateInput } from '../../../components/atoms/date-input/date-input';
import { Input } from '../../../components/atoms/input/input';
import { Select } from '../../../components/atoms/select/select';
import {
  MOEDA_SAQUE_LABEL,
  MoedaSaque,
  STATUS_SAQUE_LABEL,
  StatusSaque,
  SaquesFilters,
} from '../../../types/saque';

interface Props {
  initialValues: SaquesFilters;
  onApply: (values: SaquesFilters) => void;
  onClear: () => void;
}

const Wrapper = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 18px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const Actions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const statusOptions = [
  { label: 'Todos os status', value: '' },
  ...Object.entries(STATUS_SAQUE_LABEL).map(([value, label]) => ({
    label,
    value,
  })),
];

const moedaOptions = [
  { label: 'Todas as moedas', value: '' },
  ...Object.entries(MOEDA_SAQUE_LABEL).map(([value, label]) => ({
    label,
    value,
  })),
];

const toNumberOrEmpty = (value: string): number | '' =>
  value === '' ? '' : Number(value);

export const SaquesFiltersBar = ({ initialValues, onApply, onClear }: Props) => {
  const formik = useFormik<SaquesFilters>({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => onApply(values),
  });

  return (
    <Wrapper as="form" onSubmit={formik.handleSubmit} aria-label="Filtros de saques">
      <Field>
        Status
        <Select
          options={statusOptions}
          name="status"
          value={(formik.values.status ?? '').toString()}
          onChange={(e) =>
            formik.setFieldValue(
              'status',
              e.target.value === '' ? '' : (Number(e.target.value) as StatusSaque),
            )
          }
        />
      </Field>

      <Field>
        Moeda
        <Select
          options={moedaOptions}
          name="moeda"
          value={(formik.values.moeda ?? '').toString()}
          onChange={(e) =>
            formik.setFieldValue(
              'moeda',
              e.target.value === '' ? '' : (Number(e.target.value) as MoedaSaque),
            )
          }
        />
      </Field>

      <Field>
        Cliente (nome)
        <Input
          name="clienteNome"
          placeholder="Buscar por nome"
          value={formik.values.clienteNome ?? ''}
          onChange={formik.handleChange}
          startIcon={<Search size={16} />}
        />
      </Field>

      <Field>
        Cliente (e-mail)
        <Input
          name="clienteEmail"
          type="email"
          placeholder="email@cliente.com"
          value={formik.values.clienteEmail ?? ''}
          onChange={formik.handleChange}
          startIcon={<Search size={16} />}
        />
      </Field>

      <Field>
        Data início
        <DateInput
          name="dataInicio"
          value={formik.values.dataInicio ?? ''}
          onChange={formik.handleChange}
        />
      </Field>

      <Field>
        Data fim
        <DateInput
          name="dataFim"
          value={formik.values.dataFim ?? ''}
          onChange={formik.handleChange}
        />
      </Field>

      <Field>
        Valor mínimo
        <Input
          name="valorMinimo"
          type="number"
          min={0}
          step="0.01"
          inputMode="decimal"
          placeholder="0,00"
          value={
            formik.values.valorMinimo === '' ||
            formik.values.valorMinimo === undefined
              ? ''
              : String(formik.values.valorMinimo)
          }
          onChange={(e) =>
            formik.setFieldValue('valorMinimo', toNumberOrEmpty(e.target.value))
          }
        />
      </Field>

      <Field>
        Valor máximo
        <Input
          name="valorMaximo"
          type="number"
          min={0}
          step="0.01"
          inputMode="decimal"
          placeholder="0,00"
          value={
            formik.values.valorMaximo === '' ||
            formik.values.valorMaximo === undefined
              ? ''
              : String(formik.values.valorMaximo)
          }
          onChange={(e) =>
            formik.setFieldValue('valorMaximo', toNumberOrEmpty(e.target.value))
          }
        />
      </Field>

      <Actions>
        <Button
          variant="ghost"
          type="button"
          startIcon={<RefreshCw size={14} />}
          onClick={() => {
            formik.resetForm({ values: {} });
            onClear();
          }}
        >
          Limpar
        </Button>
        <Button
          variant="primary"
          type="submit"
          startIcon={<Filter size={14} />}
        >
          Aplicar filtros
        </Button>
      </Actions>
    </Wrapper>
  );
};
